import type { ReactNode } from 'react'

import { getListName } from 'analytics/enhanced-ecommerce'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'

import CruisePageCruiseLinesSection from './CruisePageCruiseLinesSection/CruisePageCruiseLinesSection'
import CruisePageDealsTileCarousel from './CruisePageDealsTileCarousel'
import CruisePageDepartureFromCarousel from './CruisePageDepartureFromCarousel/CruisePageDepartureFromCarousel'
import CruisePageDeparturesSection from './CruisePageDeparturesSection'
import CruisePageDestinationsSection from './CruisePageDestinationsSection'
import CruisePageCruiseTourCarousel from './CruisePageCruiseTourCarousel'
import CruisePageExpeditionCruisesCarousel from './CruisePageExpeditionCruisesCarousel'
import CruisePageLongHaulOceanCruisesCarousel from './CruisePageLongHaulOceanCruisesCarousel'
import CruisePageLuxuryPremiumCruisesCarousel from './CruisePageLuxuryPremiumCruisesCarousel'
import CruisePageLuxPlusOffersCarousel from './CruisePageLuxPlusOffersCarousel'
import CruisePageRiverCruisesCarousel from './CruisePageRiverCruisesCarousel'
import CruisePageRoyalCruisesCarousel from './CruisePageRoyalCruisesCarousel'
import CruisePageShortTermCruisesCarousel from './CruisePageShortTermCruisesCarousel'
import CruisePageSpecialOffersCarousel from './CruisePageSpecialOffersCarousel'
import CruisePageTopOffersCarousel from './CruisePageTopOffersCarousel'
import CruisePageBannersSection from './CruisePageBannersSection'
import CruisePageWhyBookWithUsSection from './CruisePageWhyBookWithUsSection'
import CruisePageSupportSection from './CruisePageSupportSection'
import { CruisePageContentSection as CruisePageContentSectionName } from './useCruisePageContentOrder'

const OFFER_LIST_SECTION_NAMES: Partial<Record<CruisePageContentSectionName, string>> = {
  [CruisePageContentSectionName.Deals]: 'Hero Carousel',
  [CruisePageContentSectionName.TopOffers]: 'Todays Top Cruises',
  [CruisePageContentSectionName.LuxPlusOffers]: 'LuxPlus Offers',
  [CruisePageContentSectionName.CruiseLines]: 'Featured Cruise Lines',
  [CruisePageContentSectionName.Destinations]: 'Popular cruise destinations',
  [CruisePageContentSectionName.DepartingFrom]: 'Departing From',
  [CruisePageContentSectionName.RoyalCaribbean]: 'Royal Caribbean Hot Sailings',
  [CruisePageContentSectionName.Departures]: 'Top Ports to Start Your Adventure',
  [CruisePageContentSectionName.ShortTerm]: 'Short Term Cruises',
  [CruisePageContentSectionName.LongHaulOcean]: 'Long-haul Ocean Cruises',
  [CruisePageContentSectionName.LuxuryPremium]: 'Luxury & Premium Cruises',
  [CruisePageContentSectionName.River]: 'River Cruises',
  [CruisePageContentSectionName.BestValue]: 'Best Value',
  [CruisePageContentSectionName.Expedition]: 'Expedition Cruises',
  [CruisePageContentSectionName.CruiseTour]: 'Cruise Tour',
}

interface Props {
  contentName: CruisePageContentSectionName
  isFirstContent?: boolean
}

export default function CruisePageContentSection({ contentName, isFirstContent = false }: Props) {
  const offerListName = OFFER_LIST_SECTION_NAMES[contentName]
  const tracking = useOfferListTracking(
    getListName('OfferList', 'CRUISE', offerListName ?? contentName),
  )

  let content: ReactNode

  switch (contentName) {
    case CruisePageContentSectionName.Deals:
      content = (
        <>
          <ScrollAnchor id="deals-carousel" />
          <CruisePageDealsTileCarousel />
        </>
      )
      break

    case CruisePageContentSectionName.TopOffers:
      content = <CruisePageTopOffersCarousel hasSearchBarOverlap={isFirstContent} />
      break

    case CruisePageContentSectionName.LuxPlusOffers:
      content = <CruisePageLuxPlusOffersCarousel />
      break

    case CruisePageContentSectionName.CruiseLines:
      content = <CruisePageCruiseLinesSection />
      break

    case CruisePageContentSectionName.Destinations:
      content = <CruisePageDestinationsSection />
      break

    case CruisePageContentSectionName.DepartingFrom:
      content = <CruisePageDepartureFromCarousel />
      break

    case CruisePageContentSectionName.RoyalCaribbean:
      content = <CruisePageRoyalCruisesCarousel />
      break

    case CruisePageContentSectionName.Departures:
      content = <CruisePageDeparturesSection />
      break

    case CruisePageContentSectionName.ShortTerm:
      content = <CruisePageShortTermCruisesCarousel />
      break

    case CruisePageContentSectionName.LongHaulOcean:
      content = <CruisePageLongHaulOceanCruisesCarousel />
      break

    case CruisePageContentSectionName.LuxuryPremium:
      content = <CruisePageLuxuryPremiumCruisesCarousel />
      break

    case CruisePageContentSectionName.River:
      content = <CruisePageRiverCruisesCarousel />
      break

    case CruisePageContentSectionName.BestValue:
      content = <CruisePageSpecialOffersCarousel />
      break

    case CruisePageContentSectionName.Expedition:
      content = <CruisePageExpeditionCruisesCarousel />
      break

    case CruisePageContentSectionName.CruiseTour:
      content = <CruisePageCruiseTourCarousel />
      break

    case CruisePageContentSectionName.Banners:
      content = <CruisePageBannersSection />
      break

    case CruisePageContentSectionName.WhyBookWithUs:
      content = <CruisePageWhyBookWithUsSection />
      break

    case CruisePageContentSectionName.Support:
      content = <CruisePageSupportSection />
      break
  }

  if (!offerListName) return content

  return <OfferListEventsProvider tracking={tracking}>{content}</OfferListEventsProvider>
}
